import React, { useState, useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        marginBottom: theme.spacing(3),
        position: 'relative'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
}));

export default function Advanced(props) {

    const { app } = useApp();
    const api = useApi();

    const [ club, setClub ] = useState({
        noti_profile_update_group_id: app.club.meta.noti_profile_update_group_id || 0,
        club_isolation: app.club.meta.club_isolation
    });

    const [ groups, setGroups ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const [ userId, setUserId ] = useState(0);
    const [ clubId, setClubId ] = useState(0);
    const [ levelId, setLevelId ] = useState(0);

    const classes = useStyles();

    const getGroups = () => { 
        setLoading(true);

        api.get('/admin/group')
            .then(res => setGroups(res.groups))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        getGroups();
    },  []);

    const handleChange = name => event => {

        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setClub({ ...club, [name]: value });
    };

    const postDirectoryCache = () => {
        setLoading(true);

        api.get('/admin/club/advanced/directory').finally(() => setLoading(false));
    }

    const postNotifications = () => {
        setLoading(true);

        const data = {
            noti_profile_update_group_id: club.noti_profile_update_group_id,
        }

        api.post('/admin/club/advanced/notifications', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const postUserCapabilities = () => {
        setLoading(true);

        const data = {
            club_isolation: club.club_isolation,
        }

        api.post('/admin/club/advanced/capabilities', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const postAddUser = () => {
        setLoading(true);

        const data = {
            user_id: userId,
            level_id: levelId
        }

        api.post('/admin/club/advanced/super/add/user', data).finally(() => setLoading(false));
    }

    const postAddAllUsers = () => {
        setLoading(true);

        const data = {
            club_id: clubId,
            level_id: levelId
        }

        api.post('/admin/club/advanced/super/add/club', data).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field][0];
        return "";
    }

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Advanced Settings
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    In advanced settings you can micro-adjust settings for your organization to help better tailor the experience here on Club Apps.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Member Directory
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={3}>
                        <Button
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            onClick={postDirectoryCache}
                            >
                            Update Directory
                        </Button>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography>
                            We automatically cache the member directory several times per day which allows it to be quickly loaded by members. If you have made changes to the directory and they are not being reflected yet, use this button to force the directory to refresh with a new cache. It may take a minute or two for the changes to take affect.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Club Notifications
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="select-group"
                            select
                            label="Select Notification Group for Profile Updates"
                            value={club.noti_profile_update_group_id}
                            onChange={handleChange('noti_profile_update_group_id')}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            error={isError('noti_profile_update_group_id')}
                            helperText={getErrorMessage('noti_profile_update_group_id')}
                            >
                            <MenuItem value={0}>
                                None
                            </MenuItem>
                            {groups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.saveButtonContainer}>
                    <Button
                        disabled={loading}
                        variant="contained"
                        color="primary"
                        onClick={postNotifications}
                        className={classes.saveButton}
                        size="small"
                        >
                        save
                    </Button>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            User Capabilities
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={club.club_isolation}
                                    onChange={handleChange('club_isolation')}
                                    name="onboard_membership"
                                    color="primary"
                                />
                            }
                            margin="normal"
                            label="Should users not be allowed to connect their profile to other organizations?"
                        />
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.saveButtonContainer}>
                    <Button
                        disabled={loading}
                        variant="contained"
                        color="primary"
                        onClick={postUserCapabilities}
                        className={classes.saveButton}
                        size="small"
                        >
                        save
                    </Button>
                </Typography>
            </Paper>
            {(app.user.id === 1) &&
                <Paper className={classes.paper}>
                    <Loading isLoading={loading}/>
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3" gutterBottom>
                                Super Admin Functions
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <TextField
                                label="Add User to Club by ID"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                disabled={loading}
                                variant="contained"
                                color="primary"
                                onClick={postAddUser}
                                className={classes.saveButton}
                                >
                                Add User to Club
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Add a Level ID for User"
                                value={levelId}
                                onChange={(e) => setLevelId(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Typography align="right" component="div" className={classes.saveButtonContainer}>
                        
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <TextField
                                label="Add All Users to Club by Club ID"
                                value={clubId}
                                onChange={(e) => setClubId(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                disabled={loading}
                                variant="contained"
                                color="primary"
                                onClick={postAddAllUsers}
                                className={classes.saveButton}
                                >
                                Add All Users to Club
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Add a Level ID for Users"
                                value={levelId}
                                onChange={(e) => setLevelId(e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Paper>
            }
        </form>
    )
}
